import React, { useState, useEffect, useContext } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import api from '../../services/api';
import useCompanySettings from '../../hooks/useSettings/companySettings';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, TextField, Paper, FormControl, InputLabel, Select, FormControlLabel, Switch, Chip } from '@material-ui/core';
import { format, differenceInMinutes, parseISO } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1),
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    borderRadius: '10px',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5),
      minWidth: 0,
      borderRadius: 0,
    },
  },
  button: {
    borderRadius: '10px',
  },
  dateInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(0.5),
      flex: 1,
      minWidth: 0,
    },
  },
  sortSelect: {
    minWidth: 150,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      flex: 1,
      marginRight: theme.spacing(0.5),
    },
  },
  mobileFilters: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    width: '100%',
  },
  mobileFilterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    width: '100%',
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showAll, setShowAll] = useState(() => localStorage.getItem('kanbanShowAll') === 'true');
  const isAdmin = user.profile === 'admin';
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);
  const whatsappIds = (user.whatsapps || []).map(w => w.id);
  const userWhatsappId = user.whatsappId;
  const { getAll: getAllSettings } = useCompanySettings();
  const [slaLimits, setSlaLimits] = useState({ green: 30, yellow: 60, red: 120 });
  const [filterSlaRed, setFilterSlaRed] = useState(false);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar configurações de SLA
  useEffect(() => {
    const fetchSlaSettings = async () => {
      try {
        const settings = await getAllSettings(user.companyId);
        setSlaLimits({
          green: parseInt(settings.slaGreenMinutes, 10) || 30,
          yellow: parseInt(settings.slaYellowMinutes, 10) || 60,
          red: parseInt(settings.slaRedMinutes, 10) || 120,
        });
      } catch {}
    };
    if (user?.companyId) fetchSlaSettings();
  }, [user?.companyId]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      const allWhatsappIds = whatsappIds.length > 0 ? whatsappIds : (userWhatsappId ? [userWhatsappId] : []);
      const { data } = await api.get('/ticket/kanban', {
        params: {
          queueIds: JSON.stringify(queueIds),
          whatsappIds: JSON.stringify(allWhatsappIds),
          startDate: startDate,
          endDate: endDate,
          showAll: showAll ? 'true' : 'false',
        },
      });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action)) {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  };

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    let sortedTickets = [...fetchedTickets];

    // Filtrar por SLA atrasado se ativado
    if (filterSlaRed) {
      sortedTickets = sortedTickets.filter(ticket => {
        try {
          const mins = differenceInMinutes(new Date(), parseISO(ticket.updatedAt));
          return mins > slaLimits.yellow;
        } catch {
          return false;
        }
      });
    }

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    // Aplicar ordem personalizada se existir
    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) {
      organizeLanes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder, laneOrder, loadingLaneOrder, filterSlaRed, slaLimits]);

  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);
    try {
      await api.delete(`/ticket-tags/${ticketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        toast.success(i18n.t("kanban.toasts.tagUpdated"));
      } else {
        toast.success(i18n.t("kanban.toasts.tagRemoved"));
      }

      fetchTickets();
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      toast.success(i18n.t("kanban.toasts.laneOrderUpdated"));
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error(i18n.t("kanban.toasts.laneOrderError"));
    }
  };

  const renderMobileHeader = () => (
    <div className={classes.mobileFilters}>
      <div className={classes.mobileFilterRow}>
        <Title>{i18n.t('kanban.title')}</Title>
      </div>
      <div className={classes.mobileFilterRow}>
        {isAdmin && (
          <FormControlLabel
            control={
              <Switch
                checked={showAll}
                onChange={(e) => {
                  const val = e.target.checked;
                  setShowAll(val);
                  localStorage.setItem('kanbanShowAll', String(val));
                }}
                color="primary"
                size="small"
              />
            }
            label={showAll ? i18n.t("kanban.allTickets") : i18n.t("kanban.myTickets")}
            style={{ marginRight: 8 }}
          />
        )}
        <Chip
          icon={<span style={{ fontSize: 12 }}>🔴</span>}
          label={filterSlaRed ? i18n.t("kanban.slaOverdue") : i18n.t("kanban.filterSla")}
          clickable
          onClick={() => setFilterSlaRed(!filterSlaRed)}
          color={filterSlaRed ? "secondary" : "default"}
          variant={filterSlaRed ? "default" : "outlined"}
          size="small"
          style={{ borderRadius: 20 }}
        />
      </div>
      <div className={classes.mobileFilterRow}>
        <FormControl variant="outlined" size="small" className={classes.sortSelect}>
          <InputLabel htmlFor="sort-order-select-m">{i18n.t('kanban.sortBy')}</InputLabel>
          <Select
            native
            value={sortOrder}
            onChange={handleSortOrderChange}
            label={i18n.t('kanban.sortBy')}
            inputProps={{ name: 'sortOrder', id: 'sort-order-select-m' }}
          >
            <option value="ticketNumber">{i18n.t('kanban.sortOptions.ticketNumber')}</option>
            <option value="lastMessageTime">{i18n.t('kanban.sortOptions.lastMessage')}</option>
            <option value="valorDesc">{i18n.t('kanban.sortOptions.valueDesc')}</option>
          </Select>
        </FormControl>
      </div>
      <div className={classes.mobileFilterRow}>
        <TextField
          label={i18n.t('kanban.startDate')}
          type="date"
          value={startDate}
          onChange={handleStartDateChange}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          className={classes.dateInput}
          size="small"
        />
        <TextField
          label={i18n.t('kanban.endDate')}
          type="date"
          value={endDate}
          onChange={handleEndDateChange}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          className={classes.dateInput}
          size="small"
        />
      </div>
      <div className={classes.mobileFilterRow} style={{ flexDirection: 'column' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearchClick}
          className={classes.button}
          size="small"
          style={{ width: '100%' }}
        >
          {i18n.t('kanban.search')}
        </Button>
        <Can
          role={user.profile}
          perform="dashboard:view"
          yes={() => (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddColumnClick}
              className={classes.button}
              size="small"
              style={{ width: '100%', fontSize: '0.75rem' }}
            >
              + {i18n.t('kanban.addColumns')}
            </Button>
          )}
        />
      </div>
    </div>
  );

  return (
    <MainContainer>
      {isMobile ? (
        renderMobileHeader()
      ) : (
        <MainHeader>
          <Title>{i18n.t('kanban.title')}</Title>
          <MainHeaderButtonsWrapper>
            {isAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showAll}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setShowAll(val);
                      localStorage.setItem('kanbanShowAll', String(val));
                    }}
                    color="primary"
                    size="small"
                  />
                }
                label={showAll ? i18n.t("kanban.allTickets") : i18n.t("kanban.myTickets")}
                style={{ marginRight: 16 }}
              />
            )}
            <Chip
              icon={<span style={{ fontSize: 14 }}>🔴</span>}
              label={filterSlaRed ? i18n.t("kanban.slaOverdue") : i18n.t("kanban.filterSla")}
              clickable
              onClick={() => setFilterSlaRed(!filterSlaRed)}
              color={filterSlaRed ? "secondary" : "default"}
              variant={filterSlaRed ? "default" : "outlined"}
              style={{ marginRight: 16, borderRadius: 20 }}
            />
            <FormControl
              variant="outlined"
              size="small"
              className={classes.sortSelect}
            >
              <InputLabel htmlFor="sort-order-select">{i18n.t('kanban.sortBy')}</InputLabel>
              <Select
                native
                value={sortOrder}
                onChange={handleSortOrderChange}
                label={i18n.t('kanban.sortBy')}
                inputProps={{
                  name: 'sortOrder',
                  id: 'sort-order-select',
                }}
              >
                <option value="ticketNumber">{i18n.t('kanban.sortOptions.ticketNumber')}</option>
                <option value="lastMessageTime">{i18n.t('kanban.sortOptions.lastMessage')}</option>
                <option value="valorDesc">{i18n.t('kanban.sortOptions.valueDesc')}</option>
              </Select>
            </FormControl>
            <TextField
              label={i18n.t('kanban.startDate')}
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              className={classes.dateInput}
              size="small"
            />
            <TextField
              label={i18n.t('kanban.endDate')}
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              className={classes.dateInput}
              size="small"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchClick}
              className={classes.button}
            >
              {i18n.t('kanban.search')}
            </Button>
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddColumnClick}
                  className={classes.button}
                >
                  + {i18n.t('kanban.addColumns')}
                </Button>
              )}
            />
          </MainHeaderButtonsWrapper>
        </MainHeader>
      )}
      <Paper variant="outlined" className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          slaLimits={slaLimits}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
